import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Directive, ElementRef, EventEmitter, HostListener, Input, NgModule, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

const BELOW: ConnectedPosition[] = [
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
];

const ABOVE: ConnectedPosition[] = [BELOW[1], BELOW[0]];

@Component({
  selector: 'mde-popover',
  exportAs: 'mdePopover',
  template: `
    <ng-template cdkConnectedOverlay
                 [cdkConnectedOverlayOrigin]="origin"
                 [cdkConnectedOverlayOpen]="opened"
                 [cdkConnectedOverlayPositions]="positions"
                 (overlayOutsideClick)="onOutsideClick($event)">
      <div class="popover-content" (click)="onContentClick()"><ng-content></ng-content></div>
    </ng-template>
  `,
  styles: [`.popover-content { background: white; box-shadow: 0 4px 12px rgba(0, 0, 0, .2); }`],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MdePopover implements OnDestroy {
  @Input() mdePopoverCloseOnClick = true;
  @Input() mdePopoverOverlapTrigger = false;
  @Input() mdePopoverPositionY: 'above' | 'below' = 'below';

  origin!: ElementRef;
  opened = false;
  readonly close = new EventEmitter<void>();
  private readonly closeSubscription: Subscription;

  get positions(): ConnectedPosition[] {
    return this.mdePopoverPositionY === 'above' ? ABOVE : BELOW;
  }

  constructor() {
    this.closeSubscription = this.close.subscribe(() => this.opened = false);
  }

  onContentClick(): void {
    if (this.mdePopoverCloseOnClick) {
      this.close.emit();
    }
  }

  onOutsideClick(event: MouseEvent): void {
    if (!this.origin.nativeElement.contains(event.target)) {
      this.close.emit();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.opened) {
      this.close.emit();
    }
  }

  ngOnDestroy(): void {
    this.closeSubscription.unsubscribe();
  }
}

@Directive({
  selector: '[mdePopoverTriggerFor]',
  standalone: false,
})
export class MdePopoverTrigger {
  @Input() mdePopoverTriggerFor!: MdePopover;

  private readonly origin: ElementRef;

  constructor(elementRef: ElementRef) {
    this.origin = elementRef;
  }

  @HostListener('click')
  onClick(): void {
    const popover = this.mdePopoverTriggerFor;
    popover.origin = this.origin;
    popover.opened = !popover.opened;
  }
}

@NgModule({
  declarations: [MdePopover, MdePopoverTrigger],
  imports: [CommonModule, OverlayModule],
  exports: [MdePopover, MdePopoverTrigger],
})
export class MdePopoverModule {}
